/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import {
  Breadcrumbs,
  Box,
  Typography,
  FormControl,
  Select,
  Tooltip,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import {
  StyledBreadCrumbContainer,
  StyledLink,
  AuditLogPageContainer,
  viewTypographyStyles,
  StyledInputLabel,
  selectBaseStyles,
  StyledTab,
  StatusContainer,
  StyledImportCount,
  datePickerStyles,
} from "./styles";
import { FilterContainer } from "../../components/filter_container";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { UpdateButton } from "../../components/update_button";
import { MenuHeading } from "../../components/menu_heading";
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";
import { useInfiniteQuery, useQuery, keepPreviousData } from "@tanstack/react-query";
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { IndexTable } from "../../components/index_table";
import { Loader } from "../../components/loader";
import camelize from "../../utils/camelize";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { AuditLogModal } from "../../components/audit_log_modal";
import DropdownSearchBar from "../../components/dorpdown_searchbar";
import { ResetButton } from "../../components/reset_button";
import { AuditImportDetail } from "../../components/audit_import_modal";
import { formatDate } from '../../utils/formatDate'
import ClearIcon from '@mui/icons-material/Clear';
import { SelectedMenuItem } from "../../components/selected_menu_item";
import { capitalize } from 'lodash'
import { buildQueryParams } from "../../utils/buildQueryParams";
import { matchesNormalizedSearch } from "../../utils/normalizeSearchTerm";
import dayjs from "dayjs";
import { useNotificationContext } from "../../contexts/notificationContext";
import { StyledMenuItem } from "../../components/styled_menu_item";
import { Query_Keys } from "../../lib/queryKeys";
import { PerPage } from "../../constants/perPage";
import { AUDIT_LOG_MODULES, DEFAULT_AUDIT_LOG_MODULE } from "../../constants/auditLogModules";
import { mergeOptionsIfChanged, getDropdownNextPageParam, normalizeRestaurant, normalizeRestaurantGroup } from "../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { StyledTabPanel } from "../user_management/styles"
import { isValidDayjsValue } from "../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../utils/isValidCustomDateRange";
import { AuditLogAnalyticsCards } from "../../components/audit_log_analytics_cards";

const CLICKABLE_CARDS = ['total_runs', 'successful_runs', 'failed_runs', 'automatic_runs', 'manual_runs', 'records_imported']
const STATUS_CARDS = ['successful_runs', 'failed_runs']
const FETCH_TYPE_CARDS = ['automatic_runs', 'manual_runs']

export const AuditLogsPage = () => {
  const singleRestaurantAutoApplied = React.useRef(false)
  const [tab, setTab] = React.useState(DEFAULT_AUDIT_LOG_MODULE.value);
  const [moduleName, setModuleName] = React.useState(DEFAULT_AUDIT_LOG_MODULE.module)
  const [filters, setFilters] = React.useState({});
  const [appliedFilters, setAppliedFilters] = React.useState({})
  const [filtersChanged, setFiltersChanged] = React.useState(false);
  const [itemsPerPage, setItemsPerPage] = React.useState(25);
  const [page, setPage] = React.useState(1);
  const [orderBy, setOrderBy] = React.useState('performed_at');
  const [order, setOrder] = React.useState('desc');
  const [jobId, setJobId] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [restaurantSearch, setRestaurantSearch] = React.useState("");
  const [selectedRestaurantGroup, setSelectedRestaurantGroup] = React.useState(null);
  const [restaurantGroupSearch, setRestaurantGroupSearch] = React.useState("");
  const [startDate, setStartDate] = React.useState(null)
  const [endDate, setEndDate] = React.useState(null)
  const [openImport, setOpenImport] = React.useState(false)
  const [importJobId, setImportJobId] = React.useState(0)
  const [restaurantName, setRestaurantName] = React.useState('')
  const [csvLoading, setCsvLoading] = React.useState(false)
  const [restaurantOptionsCache, setRestaurantOptionsCache] = React.useState([])
  const showNotification = useNotificationContext()
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [selectedCard, setSelectedCard] = React.useState('total_runs');
  const status = [
    { label: "Success", value: "success" },
    { label: "Failed", value: "failed" },
  ]
  const eventType = [
    {
      label: 'Automated Fetch',
      value: 'automated',
    },
    {
      label: 'Manual Fetch',
      value: 'manual',
    }
  ]

  const handleCardClick = (cardValue) => {
    if (!CLICKABLE_CARDS.includes(cardValue)) return;
    setSelectedCard(cardValue);
    setPage(1)
    setItemsPerPage(25)
  };

  const fetchRestaurantGroups = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    })

    return response?.data?.map(normalizeRestaurantGroup) || []
  }

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const res = await api.get('/v1/admin/dropdowns/restaurant_dropdown_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        sort_alphabetically: true,
        restaurant_group_ids: selectedRestaurantGroup || undefined,
      }
    })
    return res?.data?.map(normalizeRestaurant) || []
  }

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: 'audit_logs_filter',
    }),
    queryFn: fetchRestaurantGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants({
      sort_alphabetically: true,
      search: debouncedRestaurantSearch,
      restaurant_group_id: selectedRestaurantGroup,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const getRestaurantGroupName = (id) => {
    const group = restaurantGroups?.find((g) => g.label === id);
    return group ? group?.value : "";
  };

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const cardFilters = React.useMemo(() => {
    switch (selectedCard) {
      case "successful_runs":
        return { status: "success" };
      case "failed_runs":
        return { status: "failed" };
      case "automatic_runs":
        return { fetch_type: "automated" };
      case "manual_runs":
        return { fetch_type: "manual" };
      case "records_imported":
        return { records_imported: true };
      default:
        return {};
    }
  }, [selectedCard]);

  const hasCardFilterConflict = React.useMemo(() => {
    if (STATUS_CARDS.includes(selectedCard) && appliedFilters?.status && cardFilters?.status && appliedFilters.status !== cardFilters.status) {
      return true;
    }
    if (FETCH_TYPE_CARDS.includes(selectedCard) && appliedFilters?.fetch_type && cardFilters?.fetch_type && appliedFilters.fetch_type !== cardFilters.fetch_type) {
      return true;
    }
    return false;
  }, [selectedCard, appliedFilters, cardFilters]);

  const fetchAuditLogs = async () => {
    if (hasCardFilterConflict) {
      return { data: [], pageCount: 0 };
    }
    const { from_date, to_date, ...restFilters } = appliedFilters;
    const response = await api.get(`/v1/admin/session_logs?module_name=${moduleName}`, {
      params: {
        per_page: itemsPerPage,
        page: page,
        sort_by: orderBy,
        sort_order: order,
        ...appliedFilters,
        ...restFilters,
        ...cardFilters,
        ...(from_date ? { from_date } : {}),
        ...(to_date ? { to_date } : {}),
      },
    })
    const result = {
      data: response?.data,
      pageCount: response?.headers["total-count"]
    }
    return result
  }

  const { data: queryResult, isFetching } = useQuery({
    queryKey: Query_Keys.audit_logs({
      queryFilter: appliedFilters,
      cardFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      moduleName: moduleName,
      orderBy,
      order,
    }),
    queryFn: fetchAuditLogs,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const data = queryResult?.data;

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurants, 'id', ['name']));
  }, [restaurants])

  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && restaurants?.length === 1;

  useEffect(() => {
    if (filters?.restaurant_id || singleRestaurantAutoApplied.current || !hasSingleAvailableRestaurant) {
      return
    }

    singleRestaurantAutoApplied.current = true
    setFilters(prev => ({...prev, restaurant_id: restaurants?.[0]?.id }));
    setAppliedFilters(prev => ({...prev, restaurant_id: restaurants?.[0]?.id }));
  }, [filters?.restaurant_id, hasSingleAvailableRestaurant, restaurants])

  const fetchAuditSummary = async (id) => {
    const response = await api.get(`/v1/admin/session_logs/${id}`)
    return response?.data
  }

  const fetchAuditImport = async (id) => {
    const response = await api.get(`/v1/admin/session_logs/${id}/import_details`)
    return response?.data
  }

  const { data: auditSummary, isFetching: auditSummaryFetching } = useQuery({
    queryKey: Query_Keys.audit_log({
      job_id: jobId
    }),
    queryFn: () => fetchAuditSummary(jobId),
    staleTime: 10 * 60 * 1000,
    enabled: open
  })

  const { data: auditImport, isFetching: auditImportFetching } = useQuery({
    queryKey: Query_Keys.audit_import({
      import_job_id: importJobId
    }),
    queryFn: () => fetchAuditImport(importJobId),
    staleTime: 10 * 60 * 1000,
    enabled: openImport,
  })

  const fetchAuditLogAnalytics = async () => {
    const response = await api.get('/v1/admin/session_logs/summary', {
      params: {
        ...appliedFilters,
        module_name: moduleName
      }
    });

    return response?.data;
  };

  const { data: auditLogAnalytics, isFetching: analyticsFetching } = useQuery({
    queryKey: Query_Keys.audit_logs_analytics({
      filters: appliedFilters,
      moduleName: moduleName
    }),
    queryFn: fetchAuditLogAnalytics,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

   const handleChange = (event, newValue) => {
    const selected = AUDIT_LOG_MODULES.find((auditModule) => auditModule.value === newValue)

    if (selected) setModuleName(selected.module)

    setTab(newValue)
    // Each module has its own row count, so carrying the page across tabs lands the operator on an
    // empty page of a module that has fewer of them.
    setPage(1)
  }

  const handleStartDateChange = (date) => {
    const isValidStartDate = isValidDayjsValue(date);
    const nextEndDate = !date
      ? null
      : (isValidStartDate && !isValidDayjsValue(endDate) ? dayjs().startOf("day") : endDate);

    setStartDate(date);
    setEndDate(nextEndDate);
    setFilters((prev) => ({
      ...prev,
      from_date: isValidStartDate ? `${date.date()}/${date.month() + 1}/${date.year()}` : null,
      to_date: isValidDayjsValue(nextEndDate)
        ? `${nextEndDate.date()}/${nextEndDate.month() + 1}/${nextEndDate.year()}`
        : null,
    }));
    setFiltersChanged(true)
  }

  const handleToDateChange = (date) => {
    setEndDate(date);
    setFilters((prev) => ({
      ...prev,
      to_date: isValidDayjsValue(date) ? `${date.date()}/${date.month() + 1}/${date.year()}` : null,
    }));
    setFiltersChanged(true)
  }

  const handleStatusChange = (event) => {
    setFilters({...filters, status: event.target.value})
    setFiltersChanged(true)
  }

  const handleEventTypeChange = (event) => {
    setFilters({...filters, fetch_type: event.target.value})
    setFiltersChanged(true)
  }

  const handleStatusClear = () => {
    setFilters({...filters, status: null})
    setFiltersChanged(true)
  }

  const handleEventTypeClear = () => {
    setFilters({...filters, fetch_type: ''})
    setFiltersChanged(true)
  }

  const isCustomDateRangeValid =
    (startDate == null || isValidDayjsValue(startDate)) &&
    (endDate == null || isValidDayjsValue(endDate)) &&
    (startDate == null || endDate == null || isValidCustomDateRange(startDate, endDate));

  const isUpdateDisabled = !isCustomDateRangeValid;

  const handleRestaurantGroupChange = (event) => {
    const groupId = event.target.value;
    setSelectedRestaurantGroup(groupId);
    
    setFilters(prev => {
      const updatedFilters = { ...prev, restaurant_group_id: groupId };
      if (groupId && prev.restaurant_id) {
        const selectedRes = restaurants?.find(r => r.id === prev.restaurant_id);
        const resGroupId = selectedRes?.restaurant_group?.id || selectedRes?.restaurant_group_id;
        if (resGroupId !== groupId) {
          updatedFilters.restaurant_id = null;
        }
      }
      return updatedFilters;
    });

    setFiltersChanged(true);
  };

  const handleRestaurantGroupClear = () => {
    setSelectedRestaurantGroup(null);
    setFilters(prev => ({ ...prev, restaurant_group_id: null }));
    setFiltersChanged(true);
  }

  const handleRestaurantChange = (event) => {
    setFilters({...filters, restaurant_id: event.target.value})
    setFiltersChanged(true)
  }

  const handleRestaurantClear = () => {
    setFilters({...filters, restaurant_id: null})
    setFiltersChanged(true)
  }

  const handleImportShow = (data, id, restaurantName) => {
    if (data === 0) return
    setOpenImport(true)
    setImportJobId(id)
    // The matched option carries its restaurant group, which the destination page needs to load its
    // own restaurant dropdown. When the row's restaurant is not cached we can still pass the group
    // the operator filtered by, so the destination has something to open on.
    const restaurant = restaurantOptionsCache.find((data) => {
      return data?.name === restaurantName
    }) || { name: restaurantName, restaurant_group_id: selectedRestaurantGroup || undefined }
    setRestaurantName(restaurant)
  }

  const getEventTypeLabel = (value) => {
    const eventName = eventType?.find((event) => event?.value === value)
    return eventName?.label
  }

  const handleUpdate = () => {
    setFiltersChanged(false)
    setItemsPerPage(25);
    setAppliedFilters(filters)
    setPage(1);
    setOrderBy('performed_at')
    setOrder('desc')
  }

  const filteredRestaurantList = restaurants?.filter((restaurant) => {
    if (selectedRestaurantGroup) {
      const resGroupId = restaurant?.restaurant_group?.id || restaurant?.restaurant_group_id;
      if (resGroupId !== selectedRestaurantGroup) {
        return false;
      }
    }
    return true;
  });

  const filteredRestaurant = filteredRestaurantList?.filter((restaurant) => {
    return matchesNormalizedSearch(restaurant?.name, restaurantSearch);
  })

  const handleReset = () => {
    setFiltersChanged(false)
    setAppliedFilters({})
    setFilters({})
    setSelectedRestaurantGroup(null);
    setStartDate(null)
    setEndDate(null)
    setItemsPerPage(25);
    setPage(1);
    setOrderBy('performed_at')
    setOrder('desc')
    setSelectedCard('total_runs')
  }

  const handleSortChange = (columnKey) => {
    const nextOrder = orderBy === columnKey ? (order === 'asc' ? 'desc' : 'asc') : 'asc'
    setOrderBy(columnKey)
    setOrder(nextOrder)
    setPage(1)
  }

  const handleOpen = (id) => {
    setJobId(id)
    setOpen(true)
  }

  const getRestaurantName = (id) => {
    const restaurant = restaurantOptionsCache.find((res) => res.id === id)
    return restaurant ? restaurant?.name : ''
  }

  const splitCamelCase = (str) => {
    return str.replace(/([a-z])([A-Z])/g, '$1 $2');
  };

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const { from_date, to_date, ...restFilters } = filters;
      const newFormattedFilters = {
       module_name: moduleName,
       sort_by: orderBy,
       sort_order: order,
       ...appliedFilters,
       ...restFilters,
       ...cardFilters,
       ...(from_date ? { from_date } : {}),
       ...(to_date ? { to_date } : {}),
      }
      const query = buildQueryParams(newFormattedFilters)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=session_logs&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `session_logs_${moduleName}_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const tableColumns = [
    { key: "id", sortable: true },
    { key: "performed_at", sortable: true },
    { key: "restaurant", sortable: true },
    { key: "event_type", sortable: true },
    { key: "status", sortable: true },
    { key: "duration", sortable: true },
    { key: "no_of_retries", sortable: true },
    { key: "import_count", sortable: true },
    { key: "action", sortable: false },
  ];

  const columns = tableColumns?.map((column) => {
    return {
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable,
    };
  });

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        performed_at: formatDate(record?.created_at, record?.time_zone_abbr),
        restaurant: record?.restaurant_name,
        event_type: splitCamelCase(record?.worker_name),
        status: {
          display: (
            <StatusContainer status={record?.status}>
              {camelize(record?.status)}
            </StatusContainer>
          ),
          sortValue: record?.status,
        },
        duration: record?.duration,
        no_of_retries: record?.retry_count !== 0 ? record?.retry_count + 1 :  record?.retry_count,
        import_count: {
          display: (
            <StyledImportCount
              data-testid={`audit-import-count-${record?.id}`}
              onClick={() => handleImportShow(record?.imported_data_identifier, record?.id, record?.restaurant_name)}
            >
              {record?.imported_data_identifier}
            </StyledImportCount>
          ),
          sortValue: record?.imported_data_identifier,
        },
        action: (
          <div>
            <Tooltip title='View' arrow placement="bottom">
              <VisibilityIcon 
                data-testid={`audit-log-view-${record?.id}`}
                sx={{
                  color: '#707070',
                  cursor: 'pointer',
                }}
                onClick={() => handleOpen(record?.id)}
              />
            </Tooltip>
          </div>
        )
      };
    });
  };

  const rows = TableRows(data);

  const auditLogsTable = isFetching ? (
    <Loader />
  ) : (
    <IndexTable
      columns={columns}
      data={rows}
      tableId="log-entries"
      type="entries"
      itemsPerPage={itemsPerPage}
      setItemsPerPage={setItemsPerPage}
      page={page}
      setPage={setPage}
      pageCount={queryResult?.pageCount}
      report={false}
      showDownloadButton={true}
      disableDownloadButton={data?.length === 0 || csvLoading}
      handleExportCsv={handleExportCsv}
      csvLoading={csvLoading}
      serverSort={true}
      onSortChange={handleSortChange}
      serverOrderBy={orderBy}
      serverOrder={order}
    />
  )

  return (
    <React.Fragment>
      <AuditLogPageContainer>
        {/* Bread Crumbs */}
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink 
              LinkComponent={StyledLink} 
              label="Home" to="/admin/dashboard" 
            />
            <StyledLink color={'#515152'}>
              Logs
            </StyledLink>
            <StyledLink color={'#515152'}>
              Audit Logs
            </StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        {/* Filter Section */}
        <FilterContainer>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "18px" },
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                flex: 1
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  ...viewTypographyStyles,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  whiteSpace: "nowrap",
                  mt: 0
                }}
              >
                View
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  width: { xs: "100%", sm: "auto" }
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={startDate}
                    onChange={handleStartDateChange}
                    slotProps={{
                      textField: {
                        placeholder: "Date From",
                        inputProps: {
                          'data-testid': 'audit-date-from-filter'
                        }
                      },
                    }}
                    sx={datePickerStyles}
                  />
                </LocalizationProvider>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  width: { xs: "100%", sm: "auto" }
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={endDate}
                    onChange={handleToDateChange}
                    slotProps={{
                      textField: {
                        placeholder: "Date To",
                        inputProps: {
                          'data-testid': 'audit-date-to-filter'
                        }
                      },
                    }}
                    sx={datePickerStyles}
                  />
                </LocalizationProvider>
              </Box>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "180px" }
                }}
              >
                <StyledInputLabel>Restaurant Group</StyledInputLabel>
                <Select
                  data-testid="audit-restaurant-group-filter"
                  value={selectedRestaurantGroup || ''}
                  renderValue={() =>
                    getRestaurantGroupName(selectedRestaurantGroup)
                  }
                  label="Restaurant Group"
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "180px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantGroupChange}
                  onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                  endAdornment= {
                    selectedRestaurantGroup ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleRestaurantGroupClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant groups'} isFetching={restaurantGroupFetching}/>
                  {restaurantGroups?.map((group) => (
                    <StyledMenuItem
                      key={group?.label}
                      value={group?.label}
                    >
                      {
                        selectedRestaurantGroup === group?.label ? <SelectedMenuItem data={group?.value} /> : group?.value
                      }
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={restaurantGroupLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "180px" }
                }}
              >
                <StyledInputLabel>Restaurant</StyledInputLabel>
                <Select
                  data-testid="audit-restaurant-filter"
                  value={hasSingleAvailableRestaurant ? restaurants?.[0]?.id : (filters?.restaurant_id || '')}
                  renderValue={() =>
                    hasSingleAvailableRestaurant ? restaurants?.[0]?.name : getRestaurantName(filters?.restaurant_id)
                  }
                  label="Restaurant"
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "180px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantChange}
                  onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                  endAdornment= {
                    filters?.restaurant_id ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleRestaurantClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                  {restaurants.map((res) => (
                    <StyledMenuItem
                      key={res?.id}
                      value={res?.id}
                    >
                      {
                        hasSingleAvailableRestaurant || filters?.restaurant_id === res?.id ? <SelectedMenuItem data={res?.name} /> : res?.name
                      }
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantsPage}
                    targetRef={restaurantLoaderRef}
                  />
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "130px" }
                }}
              >
                <StyledInputLabel>Status</StyledInputLabel>
                <Select
                  data-testid="audit-status-filter"
                  value={filters?.status || ''}
                  renderValue={() =>
                    capitalize(filters?.status)
                  }
                  size="small"
                  label="Status"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { sx: { width: '220px' } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "130px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleStatusChange}
                  endAdornment= {
                    filters?.status ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleStatusClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  {status?.map((status) => (
                    <StyledMenuItem
                      key={status?.value}
                      value={status?.value}
                    >
                      {
                        filters?.status === status?.value ? <SelectedMenuItem data={status?.label} /> : status?.label
                      }
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { xs: "100%", sm: "160px" }
                }}
              >
                <StyledInputLabel>Event Type</StyledInputLabel>
                <Select
                  data-testid="audit-event-type-filter"
                  value={filters?.fetch_type || ''}
                  renderValue={() =>
                    getEventTypeLabel(filters?.fetch_type)
                  }
                  size="small"
                  label="Event Type"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { sx: { width: '220px' } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: { xs: "100%", sm: "160px" },
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleEventTypeChange}
                  endAdornment= {
                    filters?.fetch_type ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleEventTypeClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  {eventType?.map((event) => (
                    <StyledMenuItem
                      key={event?.value}
                      value={event?.value}
                    >
                      {
                        filters?.fetch_type === event?.value ? <SelectedMenuItem data={event?.label} /> : event?.label
                      }
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                alignSelf: { xs: "flex-start", md: "center" },
                mt: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" }
              }}
            >
              <ResetButton
                onClick={handleReset}
                filtersChanged={filtersChanged}
                status={isFetching}
              />

              <UpdateButton
                onClick={handleUpdate}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </FilterContainer>

        <MenuHeading report={false} dataTestId="audit-logs-page-title">Audit Logs</MenuHeading>

        <AuditLogAnalyticsCards
          isMobile={isMobile}
          data={auditLogAnalytics}
          loading={analyticsFetching}
          CLICKABLE_CARDS={CLICKABLE_CARDS}
          selectedCard={selectedCard}
          handleCardClick={handleCardClick}
        />

        {/* Tabs */}
        <TabContext value={tab}>
          <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
            <TabList onChange={handleChange} aria-label="audit log modules" variant="scrollable" scrollButtons="auto">
              {AUDIT_LOG_MODULES.map((auditModule) => (
                <StyledTab
                  key={auditModule.value}
                  label={auditModule.tabLabel}
                  value={auditModule.value}
                />
              ))}
            </TabList>
          </Box>
          {AUDIT_LOG_MODULES.map((auditModule) => (
            <StyledTabPanel key={auditModule.value} value={auditModule.value}>
              {auditLogsTable}
            </StyledTabPanel>
          ))}
        </TabContext>
      </AuditLogPageContainer>

      {/* Audit Log Modal */}
      <AuditLogModal
        open={open}
        setOpen={setOpen}
        moduleName={moduleName}
        auditRow={auditSummary}
        isLoading={auditSummaryFetching}
       />

       {/* Audit Import Modal */}
       <AuditImportDetail
        open={openImport}
        setOpen={setOpenImport}
        moduleName={moduleName}
        details={auditImport}
        isLoading={auditImportFetching}
        restaurant={restaurantName}
       />
    </React.Fragment>
  )
}
